import _ from "lodash";
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import classNames from "classnames";
import { connect } from "react-redux";

import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { TwoLinesOutlined, CloseOutlined } from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";

import {
  markConversationAsRead,
  setShowUnreadMessageButton,
  generateSummaryForNewMessages,
  getMessagingV2ReduxState,
} from "MessagingV2/modules/MessagingModule";

import { getIsAISummariseEnabled } from "MessagingV2/modules/Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

import classes from "./UnreadMessageLine.scss";

const UnreadMessageLine = ({
  messageId,
  unreadMessageCount,
  showUnreadMessageButton,
  showSummariseNewMessagesWithAi,
}) => {
  const { t } = useI18n(["common"]);
  const containerRef = useRef();
  const { chatId } = useChatOptions();
  const dispatch = useDispatch();
  const [showSummariesWithAi, setShowSummariesWithAi] = useState(false);

  useLayoutEffect(() => {
    const node = containerRef.current;
    // as soon as the line is visible, mark the conversation as read
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          dispatch(setShowUnreadMessageButton(false));
          markConversationAsRead({ chatId });
        }
      },
      { threshold: 0.5 }
    );

    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
  }, [chatId, dispatch]);

  useEffect(() => {
    if (_.gte(unreadMessageCount, 3)) {
      setShowSummariesWithAi(true);
    }
  }, [unreadMessageCount]);

  const showSummariesWithAiButton =
    !showUnreadMessageButton &&
    showSummariesWithAi &&
    showSummariseNewMessagesWithAi;

  const onGenerateSummaryClick = () => {
    setShowSummariesWithAi(false);
    dispatch(
      generateSummaryForNewMessages({
        chatId,
        messageId,
      })
    );
  };

  const onHideSummariesWithAi = event => {
    event?.stopPropagation();
    setShowSummariesWithAi(false);
  };

  const isAISummariseEnabled = getIsAISummariseEnabled();

  const handleGenerateSummaryKeyDown = e =>
    onEnterOrSpaceKeyDown(e, onGenerateSummaryClick);

  return (
    <>
      <div ref={containerRef} className={classes.container} id="">
        <div className={classes.line} />
        <div className={classNames("text-body-3", classes.text)}>
          {t("common:new")}
        </div>
        <div className={classes.line} />
      </div>
      {showSummariesWithAiButton && isAISummariseEnabled ? (
        <div
          className={classes.newMessageButtonContainer}
          role="button"
          tabIndex={0}
          onClick={onGenerateSummaryClick}
          onKeyDown={handleGenerateSummaryKeyDown}
        >
          <TwoLinesOutlined size="xx-small" variant="success" />
          <div
            className={classNames("text-label-3", classes.summaryWithAiText)}
          >
            {t("common:summarise_new_messages_with_ai")}
          </div>
          <IconButton
            variant="plain"
            icon={<CloseOutlined />}
            onClick={onHideSummariesWithAi}
            size="xx-small"
          />
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = state => {
  const messagingReduxState = getMessagingV2ReduxState(state);

  const showUnreadMessageButton = _.get(
    messagingReduxState,
    "showUnreadMessageButton",
    false
  );
  const showSummariseNewMessagesWithAi = _.get(
    messagingReduxState,
    "showSummariseNewMessagesWithAi",
    false
  );
  return {
    showUnreadMessageButton,
    showSummariseNewMessagesWithAi,
  };
};

export default connect(mapStateToProps)(UnreadMessageLine);
